import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Page from "./Page";
import EntityList from "./EntityList";
import Dialog_Flex from "../ui/Dialog_Flex";
import Input from "../elements/Input";
import Select from "../elements/Select";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const SECTION_KEYS = [
  "authConfig",
  "endpointConfig",
  "retryConfig",
  "ackConfig",
  "poNumberConfig",
  "mappingProfile",
];

const getDefaultPoNumberConfig = () => ({
  prefix: "",
  separator: "",
  padLength: 0,
  startAt: 1,
  lastPoNumber: 0,
});

const TRANSPORT_ITEMS = [
  { id: "email", name: "Email" },
  { id: "sftp", name: "SFTP" },
];

const MAPPING_MODES = [
  { id: "json_single", name: "JSON Single" },
  { id: "csv_single", name: "CSV Single" },
  { id: "csv_header_detail", name: "CSV Header + Detail" },
  { id: "csv_hd_single_file", name: "CSV Header + Detail (Single File)" },
  { id: "xml_single", name: "XML Single" },
  { id: "edi_850", name: "EDI 850" },
];

const MAPPED_VALUE_TYPE_ITEMS = [
  { id: "path", name: "Path" },
  { id: "literal", name: "Constant" },
];

const getTemplatesByTransport = (transportType) => {
  const commonRetry = {
    maxAttempts: 5,
    initialDelayMs: 30000,
    backoffMultiplier: 2,
    maxDelayMs: 900000,
    retryOnStatuses: [408, 429, 500, 502, 503, 504],
  };

  const commonAck = {
    ackRequired: true,
    mode: "poll",
    pollIntervalMs: 60000,
    timeoutMs: 3600000,
    successCodes: ["ACCEPTED"],
    failureCodes: ["REJECTED"],
  };

  const commonMapping = {
    version: "v1",
    mode: "json_single",
    quoteHeaders: true,
    headerFields: [
      {
        source: "order.localId",
        label: "OrderNumber",
        required: true,
        isJoinKey: true,
        transform: null,
      },
      {
        source: "location.name",
        label: "storeName",
        required: false,
        transform: null,
      },
      {
        source: "order.transmittedAt",
        label: "orderDate",
        required: false,
        transform: { name: "dateFormat", args: { pattern: "yyyy-MM-dd" } },
      },
    ],
    detailFields: [
      {
        source: "line.orderNumber",
        label: "OrderNumber",
        required: true,
        transform: null,
      },
      {
        source: "line.lineNumber",
        label: "lineNumber",
        required: true,
        transform: null,
      },
      {
        source: "line.sku.name",
        label: "skuName",
        required: false,
        transform: null,
      },
      {
        source: "line.quantity",
        label: "quantity",
        required: true,
        transform: null,
      },
    ],
    trailerFields: [],
  };

  if (transportType === "sftp") {
    return {
      authConfig: {
        authType: "password",
        username: "",
        password: "",
      },
      endpointConfig: {
        host: "",
        port: 22,
        remotePath: "/inbound/orders",
        filenamePattern: "orders-{yyyyMMdd-HHmmss}.json",
        timeoutMs: 30000,
      },
      retryConfig: commonRetry,
      ackConfig: commonAck,
      mappingProfile: commonMapping,
    };
  }

  if (transportType === "email") {
    return {
      authConfig: {
        email: "",
        password: "",
      },
      endpointConfig: {
        to: [],
        subjectTemplate: "Order {orderNumber}",
        bodyTemplate: "Please find attached order {orderNumber}.",
        attachmentFormat: "json",
      },
      retryConfig: commonRetry,
      ackConfig: {
        ackRequired: false,
        mode: "none",
      },
      mappingProfile: commonMapping,
    };
  }

  return getTemplatesByTransport("email");
};

const stringifyPretty = (value) => {
  return JSON.stringify(value || {}, null, 2);
};

const parseJsonOrError = (value, label) => {
  try {
    const parsed = JSON.parse(String(value || "{}"));
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { error: `${label} must be a JSON object.` };
    }
    return { value: parsed };
  } catch (err) {
    return { error: `${label} contains invalid JSON.` };
  }
};

const parseTransformInputValue = (rawValue) => {
  const input = String(rawValue || "").trim();
  if (!input) return null;

  const normalizeDatePatternInput = (patternInput) => {
    let pattern = String(patternInput || "").trim();
    if (!pattern) return "yyyy-MM-dd";

    pattern = pattern.replaceAll("YYYY", "yyyy").replaceAll("DD", "dd");

    // Common admin shorthand uses lowercase mm for month; keep minute tokens
    // untouched when an explicit hour token is present.
    if (!/[Hh]/.test(pattern)) {
      pattern = pattern.replaceAll("mm", "MM");
    }

    return pattern;
  };

  const wrappedDatePatternMatch = input.match(/^\{([yYMdDhHmMsS\-_/:\s]+)\}$/);
  if (wrappedDatePatternMatch) {
    return {
      name: "dateFormat",
      args: { pattern: normalizeDatePatternInput(wrappedDatePatternMatch[1]) },
    };
  }

  const plainDatePatternMatch =
    /^[yYMdDhHmMsS\-_/:\s]+$/.test(input) && /[yY]/.test(input);
  if (plainDatePatternMatch) {
    return {
      name: "dateFormat",
      args: { pattern: normalizeDatePatternInput(input) },
    };
  }

  // Fast path: plain transform name (for example "dateFormat").
  if (!input.startsWith("{")) {
    return { name: input, args: {} };
  }

  // Accept relaxed object notation like:
  // {name:"dateFormat", args:{pattern:"yyyy-MM-dd"}}
  const normalized = input.replace(
    /([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g,
    '$1"$2":',
  );

  try {
    const parsed = JSON.parse(normalized);
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      typeof parsed.name === "string" &&
      parsed.name.trim().length > 0
    ) {
      return {
        name: parsed.name.trim(),
        args:
          parsed.args &&
          typeof parsed.args === "object" &&
          !Array.isArray(parsed.args)
            ? parsed.args
            : {},
      };
    }
  } catch (_error) {
    // Fall back to name-only transform below.
  }

  // Fallback keeps UX forgiving if partial object text is entered.
  return { name: input, args: {} };
};

const formatTransformInputValue = (transform) => {
  if (!transform || typeof transform !== "object") {
    return "";
  }

  const name = String(transform?.name || "").trim();
  if (!name) {
    return "";
  }

  if (name === "dateFormat") {
    const pattern = String(transform?.args?.pattern || "").trim();
    return pattern || name;
  }

  return name;
};

const OrdersTransmissionProfilesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreviewJson, setShowPreviewJson] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    isActive: true,
    transportType: "email",
    payloadFormatVersion: "v1",
    authConfig: stringifyPretty(getTemplatesByTransport("email").authConfig),
    endpointConfig: stringifyPretty(
      getTemplatesByTransport("email").endpointConfig,
    ),
    retryConfig: stringifyPretty(getTemplatesByTransport("email").retryConfig),
    ackConfig: stringifyPretty(getTemplatesByTransport("email").ackConfig),
    poNumberConfig: stringifyPretty(getDefaultPoNumberConfig()),
    mappingProfile: stringifyPretty(
      getTemplatesByTransport("email").mappingProfile,
    ),
  });

  useEffect(() => {
    if (!clientId) {
      setRows([]);
      return;
    }

    loadProfiles();
  }, [clientId]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => {
      const leftActive = left?.isActive ? 1 : 0;
      const rightActive = right?.isActive ? 1 : 0;
      if (leftActive !== rightActive) return rightActive - leftActive;

      return String(left?.name || "").localeCompare(String(right?.name || ""));
    });
  }, [rows]);

  const loadProfiles = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "ClientTransmissionProfile",
        action: "get",
        filter: { clientId },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setRows(results.data);
      } else {
        setRows([]);
        showMessage(results?.message || "Failed to load profiles", "error");
      }
    } catch (err) {
      setRows([]);
      showMessage(err?.message || "Failed to load profiles", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const safeParseObject = (value) => {
    try {
      const parsed = JSON.parse(String(value || "{}"));
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return parsed;
      }
      return {};
    } catch {
      return {};
    }
  };

  const getSectionObject = (sectionKey) => {
    return safeParseObject(formData?.[sectionKey]);
  };

  const updateSectionObject = (sectionKey, nextObject) => {
    updateField(sectionKey, stringifyPretty(nextObject));
  };

  const updateSectionValue = (sectionKey, key, value) => {
    const current = getSectionObject(sectionKey);
    updateSectionObject(sectionKey, {
      ...current,
      [key]: value,
    });
  };

  const runValidation = () => {
    const errors = [];

    const name = String(formData.name || "").trim();
    if (!name) {
      errors.push("Profile name is required.");
    }

    const version = String(formData.payloadFormatVersion || "").trim();
    if (!version) {
      errors.push("Payload format version is required.");
    }

    const parsedSections = {};
    for (const key of SECTION_KEYS) {
      const parsed = parseJsonOrError(formData[key], key);
      if (parsed.error) {
        errors.push(parsed.error);
      } else {
        parsedSections[key] = parsed.value;
      }
    }

    const transportType = formData.transportType;
    const authConfig = parsedSections.authConfig || {};
    const endpointConfig = parsedSections.endpointConfig || {};
    const retryConfig = parsedSections.retryConfig || {};
    const ackConfig = parsedSections.ackConfig || {};
    const mappingProfile = parsedSections.mappingProfile || {};
    const poNumberConfig = parsedSections.poNumberConfig || {};

    const transportDefaults = getTemplatesByTransport(transportType);
    const normalizedRetryConfig =
      Object.keys(retryConfig).length > 0
        ? retryConfig
        : transportDefaults.retryConfig;
    const normalizedAckConfig =
      Object.keys(ackConfig).length > 0
        ? ackConfig
        : transportDefaults.ackConfig;

    parsedSections.retryConfig = normalizedRetryConfig;
    parsedSections.ackConfig = normalizedAckConfig;

    const normalizedPadLength = Number(poNumberConfig.padLength);
    const normalizedStartAt = Number(poNumberConfig.startAt);
    const normalizedLastPoNumber = Number(poNumberConfig.lastPoNumber);

    if (!Number.isInteger(normalizedPadLength) || normalizedPadLength < 0) {
      errors.push("poNumberConfig.padLength must be an integer >= 0.");
    }

    if (!Number.isInteger(normalizedStartAt) || normalizedStartAt < 1) {
      errors.push("poNumberConfig.startAt must be an integer >= 1.");
    }

    if (
      !Number.isInteger(normalizedLastPoNumber) ||
      normalizedLastPoNumber < 0
    ) {
      errors.push("poNumberConfig.lastPoNumber must be an integer >= 0.");
    }

    if (
      Number.isInteger(normalizedStartAt) &&
      Number.isInteger(normalizedLastPoNumber) &&
      normalizedLastPoNumber < normalizedStartAt - 1
    ) {
      errors.push(
        "poNumberConfig.lastPoNumber must be >= startAt - 1 so sequence can advance correctly.",
      );
    }

    parsedSections.poNumberConfig = {
      ...getDefaultPoNumberConfig(),
      ...poNumberConfig,
      prefix: String(poNumberConfig.prefix || ""),
      separator: String(poNumberConfig.separator || ""),
      padLength: Number.isInteger(normalizedPadLength)
        ? normalizedPadLength
        : 0,
      startAt: Number.isInteger(normalizedStartAt) ? normalizedStartAt : 1,
      lastPoNumber: Number.isInteger(normalizedLastPoNumber)
        ? normalizedLastPoNumber
        : 0,
    };

    if (!["email", "sftp"].includes(transportType)) {
      errors.push("transportType must be email or sftp.");
    }

    if (transportType === "sftp") {
      if (!String(authConfig.authType || "").trim()) {
        errors.push("authConfig.authType is required for SFTP transport.");
      }
      if (!String(endpointConfig.host || "").trim()) {
        errors.push("endpointConfig.host is required for SFTP transport.");
      }
      if (!String(endpointConfig.remotePath || "").trim()) {
        errors.push(
          "endpointConfig.remotePath is required for SFTP transport.",
        );
      }
    }

    if (transportType === "email") {
      const hasRecipients =
        (Array.isArray(endpointConfig.to) && endpointConfig.to.length > 0) ||
        String(endpointConfig.to || "").trim().length > 0;

      if (!String(authConfig.email || "").trim()) {
        errors.push("authConfig.email is required for Email transport.");
      }

      if (!String(authConfig.password || "").trim()) {
        errors.push("authConfig.password is required for Email transport.");
      }

      if (!hasRecipients) {
        errors.push(
          "endpointConfig.to must include at least one recipient for Email transport.",
        );
      }

      if (!String(endpointConfig.subjectTemplate || "").trim()) {
        errors.push(
          "endpointConfig.subjectTemplate is required for Email transport.",
        );
      }
    }

    if (normalizedRetryConfig.maxAttempts === undefined) {
      errors.push("retryConfig.maxAttempts is required.");
    }

    if (typeof normalizedAckConfig.ackRequired !== "boolean") {
      errors.push("ackConfig.ackRequired must be true or false.");
    }

    if (!String(mappingProfile.version || "").trim()) {
      errors.push("mappingProfile.version is required.");
    }

    if (!String(mappingProfile.mode || "").trim()) {
      errors.push("mappingProfile.mode is required.");
    }

    if (
      Object.prototype.hasOwnProperty.call(mappingProfile, "quoteHeaders") &&
      typeof mappingProfile.quoteHeaders !== "boolean"
    ) {
      errors.push("mappingProfile.quoteHeaders must be true or false.");
    }

    if (!Array.isArray(mappingProfile.headerFields)) {
      errors.push("mappingProfile.headerFields must be an array.");
    }

    if (!Array.isArray(mappingProfile.detailFields)) {
      errors.push("mappingProfile.detailFields must be an array.");
    }

    if (mappingProfile.mode === "csv_header_detail") {
      const headerFields = Array.isArray(mappingProfile.headerFields)
        ? mappingProfile.headerFields
        : [];
      const detailFields = Array.isArray(mappingProfile.detailFields)
        ? mappingProfile.detailFields
        : [];

      const joinKeyLabels = [
        ...new Set(
          headerFields
            .filter(
              (field) =>
                field &&
                field.isJoinKey === true &&
                String(field.label || "").trim(),
            )
            .map((field) => String(field.label || "").trim()),
        ),
      ];

      if (joinKeyLabels.length === 0) {
        errors.push(
          "mappingProfile.headerFields must mark at least one field with isJoinKey=true for CSV Header + Detail mode.",
        );
      }

      const detailLabels = new Set(
        detailFields
          .map((field) => String(field?.label || "").trim())
          .filter(Boolean),
      );

      joinKeyLabels.forEach((label) => {
        if (!detailLabels.has(label)) {
          errors.push(
            `mappingProfile.detailFields must include join key label '${label}'.`,
          );
        }
      });
    }

    setValidationErrors(errors);
    return {
      isValid: errors.length === 0,
      parsedSections,
    };
  };

  const formatServerValidationError = (entry) => {
    if (!entry || typeof entry !== "object") {
      return String(entry || "Validation error");
    }

    const section = entry.section ? `[${entry.section}] ` : "";
    const field = entry.fieldLabel
      ? `${entry.fieldLabel}: `
      : entry.name
        ? `${entry.name}: `
        : "";
    return `${section}${field}${entry.message || "Validation error"}`;
  };

  const runServerValidation = async (parsedSections) => {
    try {
      const results = await executeAction({
        entityName: "ClientTransmissionProfile",
        action: "validateProfile",
        data: {
          mappingProfile: parsedSections?.mappingProfile || {},
        },
      });

      if (!results?.isSuccessful) {
        setValidationErrors([
          results?.message || "Server validation request failed.",
        ]);
        return { isValid: false, warnings: [] };
      }

      const serverErrors = Array.isArray(results?.data?.errors)
        ? results.data.errors
        : Array.isArray(results?.fieldErrors)
          ? results.fieldErrors
          : [];

      const serverWarnings = Array.isArray(results?.data?.warnings)
        ? results.data.warnings
        : [];

      if (serverErrors.length > 0) {
        setValidationErrors(serverErrors.map(formatServerValidationError));
        return { isValid: false, warnings: serverWarnings };
      }

      if (serverWarnings.length > 0) {
        setValidationErrors(serverWarnings.map(formatServerValidationError));
      } else {
        setValidationErrors([]);
      }

      return { isValid: true, warnings: serverWarnings };
    } catch (err) {
      setValidationErrors([
        err?.message || "Server validation request failed.",
      ]);
      return { isValid: false, warnings: [] };
    }
  };

  const buildPreviewContext = () => {
    const timestamp = new Date().toISOString();
    return {
      order: {
        id: "order-preview-1",
        localId: "PREVIEW-1001",
        poNumber: "PO-000001",
        submittedAt: timestamp,
        transmittedAt: timestamp,
        totalQty: 12,
        totalAmount: 123.45,
      },
      location: {
        id: "location-preview-1",
        name: "Demo Store",
      },
      client: {
        id: clientId || "client-preview",
        name: clientName || "Demo Client",
      },
      survey: {
        id: "survey-preview-1",
      },
      rep: {
        id: "rep-preview-1",
        name: "Demo Rep",
      },
      batch: {
        id: "batch-preview-1",
        batchKey: "BATCH-PREVIEW-1",
      },
    };
  };

  const buildPreviewDetailItems = () => {
    return [
      {
        lineNumber: 1,
        localId: "PREVIEW-1001",
        orderNumber: "PO-000001",
        poNumber: "PO-000001",
        quantity: 5,
        unitPrice: 9.99,
        sku: {
          name: "Demo SKU A",
          upc: "012345678901",
          clientCode: "SKU-A",
        },
      },
      {
        lineNumber: 2,
        localId: "PREVIEW-1001",
        orderNumber: "PO-000001",
        poNumber: "PO-000001",
        quantity: 7,
        unitPrice: 10.5,
        sku: {
          name: "Demo SKU B",
          upc: "109876543210",
          clientCode: "SKU-B",
        },
      },
    ];
  };

  const runPayloadPreview = async (parsedSections) => {
    setPreviewLoading(true);
    try {
      const results = await executeAction({
        entityName: "OrderTransmission",
        action: "buildPayloadPreview",
        data: {
          mappingProfile: parsedSections?.mappingProfile || {},
          context: buildPreviewContext(),
          detailItems: buildPreviewDetailItems(),
        },
      });

      if (!results?.isSuccessful) {
        setPreviewResult(null);
        setValidationErrors([
          results?.message || "Payload preview request failed.",
        ]);
        return { isValid: false };
      }

      const payload = results?.data || null;
      setPreviewResult(payload);

      const previewErrors = Array.isArray(payload?.errors)
        ? payload.errors
        : [];
      if (previewErrors.length > 0) {
        setValidationErrors(previewErrors.map(formatServerValidationError));
      } else if (payload?.isValid === false) {
        setValidationErrors([
          "Payload preview is invalid but no detailed error was returned. Check source paths and required fields.",
        ]);
      } else {
        setValidationErrors([]);
      }

      return { isValid: !!payload?.isValid };
    } catch (err) {
      setPreviewResult(null);
      setValidationErrors([err?.message || "Payload preview request failed."]);
      return { isValid: false };
    } finally {
      setPreviewLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRecord(null);
    setValidationErrors([]);
    setFormData({
      name: "",
      isActive: true,
      transportType: "email",
      payloadFormatVersion: "v1",
      authConfig: stringifyPretty(getTemplatesByTransport("email").authConfig),
      endpointConfig: stringifyPretty(
        getTemplatesByTransport("email").endpointConfig,
      ),
      retryConfig: stringifyPretty(
        getTemplatesByTransport("email").retryConfig,
      ),
      ackConfig: stringifyPretty(getTemplatesByTransport("email").ackConfig),
      poNumberConfig: stringifyPretty(getDefaultPoNumberConfig()),
      mappingProfile: stringifyPretty(
        getTemplatesByTransport("email").mappingProfile,
      ),
    });
    setOpenDialog(true);
    setPreviewResult(null);
    setShowPreviewJson(false);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setValidationErrors([]);

    const normalizedTransportType = ["email", "sftp"].includes(
      record?.transportType,
    )
      ? record.transportType
      : "email";
    const transportDefaults = getTemplatesByTransport(normalizedTransportType);
    const recordRetryConfig =
      record?.retryConfig && typeof record.retryConfig === "object"
        ? record.retryConfig
        : null;
    const recordAckConfig =
      record?.ackConfig && typeof record.ackConfig === "object"
        ? record.ackConfig
        : null;

    setFormData({
      name: record?.name || "",
      isActive: record?.isActive !== false,
      transportType: normalizedTransportType,
      payloadFormatVersion: record?.payloadFormatVersion || "v1",
      authConfig: stringifyPretty(record?.authConfig),
      endpointConfig: stringifyPretty(record?.endpointConfig),
      retryConfig: stringifyPretty(
        recordRetryConfig && Object.keys(recordRetryConfig).length > 0
          ? recordRetryConfig
          : transportDefaults.retryConfig,
      ),
      ackConfig: stringifyPretty(
        recordAckConfig && Object.keys(recordAckConfig).length > 0
          ? recordAckConfig
          : transportDefaults.ackConfig,
      ),
      poNumberConfig: stringifyPretty({
        ...getDefaultPoNumberConfig(),
        ...(record?.poNumberConfig || {}),
      }),
      mappingProfile: stringifyPretty(record?.mappingProfile),
    });

    setOpenDialog(true);
    setPreviewResult(null);
    setShowPreviewJson(false);
  };

  const renderAuthConfigEditor = () => {
    const transportType = formData.transportType;
    const auth = getSectionObject("authConfig");

    if (transportType === "email") {
      return (
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 1,
            p: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            authConfig
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Input
              label="Email Address"
              value={auth.email || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "email", value)
              }
            />
            <Input
              label="Password"
              value={auth.password || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "password", value)
              }
            />
          </Box>
        </Box>
      );
    }

    const common = (
      <Select
        label="Auth Type"
        value={auth.authType || "password"}
        onChange={(value) =>
          updateSectionValue("authConfig", "authType", value)
        }
        items={[
          { id: "password", name: "Password" },
          { id: "private_key", name: "Private Key" },
        ]}
      />
    );

    return (
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 1,
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
          authConfig
        </Typography>
        {common}

        {auth.authType === "password" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Input
              label="Username"
              value={auth.username || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "username", value)
              }
            />
            <Input
              label="Password"
              value={auth.password || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "password", value)
              }
            />
          </Box>
        )}

        {auth.authType === "private_key" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Input
              label="Username"
              value={auth.username || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "username", value)
              }
            />
            <TextField
              label="Private Key PEM"
              value={auth.privateKeyPem || ""}
              onChange={(event) =>
                updateSectionValue(
                  "authConfig",
                  "privateKeyPem",
                  event.target.value,
                )
              }
              multiline
              minRows={3}
              size="small"
              fullWidth
            />
            <Input
              label="Passphrase"
              value={auth.passphrase || ""}
              onChange={(value) =>
                updateSectionValue("authConfig", "passphrase", value)
              }
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderEndpointConfigEditor = () => {
    const transportType = formData.transportType;
    const endpoint = getSectionObject("endpointConfig");

    return (
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 1,
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
          endpointConfig
        </Typography>

        {transportType === "sftp" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Input
              label="Host"
              value={endpoint.host || ""}
              onChange={(value) =>
                updateSectionValue("endpointConfig", "host", value)
              }
            />
            <Input
              label="Port"
              value={String(endpoint.port ?? 22)}
              onChange={(value) =>
                updateSectionValue(
                  "endpointConfig",
                  "port",
                  Number(value || 22),
                )
              }
            />
            <Input
              label="Remote Path"
              value={endpoint.remotePath || ""}
              onChange={(value) =>
                updateSectionValue("endpointConfig", "remotePath", value)
              }
            />
            <Input
              label="Filename Pattern"
              value={endpoint.filenamePattern || ""}
              onChange={(value) =>
                updateSectionValue("endpointConfig", "filenamePattern", value)
              }
            />
          </Box>
        )}

        {transportType === "email" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
            >
              <Input
                label="To (comma separated)"
                value={
                  Array.isArray(endpoint.to)
                    ? endpoint.to.join(", ")
                    : String(endpoint.to || "")
                }
                onChange={(value) =>
                  updateSectionValue(
                    "endpointConfig",
                    "to",
                    value
                      .split(",")
                      .map((entry) => entry.trim())
                      .filter(Boolean),
                  )
                }
              />
              <Input
                label="Attachment Format"
                value={endpoint.attachmentFormat || "json"}
                onChange={(value) =>
                  updateSectionValue(
                    "endpointConfig",
                    "attachmentFormat",
                    value,
                  )
                }
              />
            </Box>
            <Input
              label="Subject Template"
              value={endpoint.subjectTemplate || ""}
              onChange={(value) =>
                updateSectionValue("endpointConfig", "subjectTemplate", value)
              }
            />
            <TextField
              label="Body Template"
              value={endpoint.bodyTemplate || ""}
              onChange={(event) =>
                updateSectionValue(
                  "endpointConfig",
                  "bodyTemplate",
                  event.target.value,
                )
              }
              multiline
              minRows={3}
              size="small"
              fullWidth
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderMappingProfileEditor = () => {
    const mapping = getSectionObject("mappingProfile");
    const headerFields = Array.isArray(mapping.headerFields)
      ? mapping.headerFields
      : [];
    const detailFields = Array.isArray(mapping.detailFields)
      ? mapping.detailFields
      : [];
    const trailerFields = Array.isArray(mapping.trailerFields)
      ? mapping.trailerFields
      : [];

    const addField = (sectionKey) => {
      const next = {
        sourceType: "path",
        source: "",
        literalValue: "",
        label: "",
        required: false,
        isNumeric: false,
        isJoinKey: false,
        transform: null,
      };
      const current = Array.isArray(mapping[sectionKey])
        ? mapping[sectionKey]
        : [];
      updateSectionObject("mappingProfile", {
        ...mapping,
        [sectionKey]: [...current, next],
      });
    };

    const removeField = (sectionKey, index) => {
      const current = Array.isArray(mapping[sectionKey])
        ? mapping[sectionKey]
        : [];
      updateSectionObject("mappingProfile", {
        ...mapping,
        [sectionKey]: current.filter((_entry, idx) => idx !== index),
      });
    };

    const moveField = (sectionKey, index, direction) => {
      const current = Array.isArray(mapping[sectionKey])
        ? [...mapping[sectionKey]]
        : [];

      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) return;

      const [moved] = current.splice(index, 1);
      current.splice(targetIndex, 0, moved);

      updateSectionObject("mappingProfile", {
        ...mapping,
        [sectionKey]: current,
      });
    };

    const updateMappedField = (sectionKey, index, patch) => {
      const current = Array.isArray(mapping[sectionKey])
        ? mapping[sectionKey]
        : [];
      const next = current.map((entry, idx) => {
        if (idx !== index) return entry;
        return {
          ...(entry || {}),
          ...patch,
        };
      });
      updateSectionObject("mappingProfile", {
        ...mapping,
        [sectionKey]: next,
      });
    };

    const renderFieldSection = (title, sectionKey, rows) => {
      const showJoinKey =
        sectionKey === "headerFields" && mapping.mode === "csv_header_detail";

      return (
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            p: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              {title}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => addField(sectionKey)}
            >
              Add Field
            </Button>
          </Box>

          {rows.length === 0 && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              No fields configured.
            </Typography>
          )}

          {rows.map((entry, idx) => {
            const transformName = formatTransformInputValue(entry?.transform);
            const sourceType =
              String(entry?.sourceType || "path").toLowerCase() === "literal"
                ? "literal"
                : "path";
            return (
              <Box
                key={`${sectionKey}-${idx}`}
                sx={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 1,
                  p: 1,
                  display: "grid",
                  gridTemplateColumns: showJoinKey
                    ? "1fr 1.2fr 1.2fr 0.8fr auto auto auto auto auto"
                    : "1fr 1.2fr 1.2fr 0.8fr auto auto auto auto",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Select
                  label="Value Type"
                  value={sourceType}
                  items={MAPPED_VALUE_TYPE_ITEMS}
                  onChange={(value) =>
                    updateMappedField(sectionKey, idx, {
                      sourceType: value,
                    })
                  }
                />
                <Input
                  label={sourceType === "literal" ? "Constant Value" : "Source"}
                  value={
                    sourceType === "literal"
                      ? (entry?.literalValue ?? "")
                      : entry?.source || ""
                  }
                  onChange={(value) =>
                    updateMappedField(
                      sectionKey,
                      idx,
                      sourceType === "literal"
                        ? { literalValue: value }
                        : { source: value },
                    )
                  }
                />
                <Input
                  label="Label"
                  value={entry?.label || ""}
                  onChange={(value) =>
                    updateMappedField(sectionKey, idx, { label: value })
                  }
                />
                <Input
                  label="Transform"
                  value={transformName}
                  onChange={(value) =>
                    updateMappedField(sectionKey, idx, {
                      transform: parseTransformInputValue(value),
                    })
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={entry?.required === true}
                      onChange={(_event, checked) =>
                        updateMappedField(sectionKey, idx, {
                          required: checked,
                        })
                      }
                    />
                  }
                  label="Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={entry?.isNumeric === true}
                      onChange={(_event, checked) =>
                        updateMappedField(sectionKey, idx, {
                          isNumeric: checked,
                        })
                      }
                    />
                  }
                  label="Numeric"
                />
                {showJoinKey && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry?.isJoinKey === true}
                        onChange={(_event, checked) =>
                          updateMappedField(sectionKey, idx, {
                            isJoinKey: checked,
                          })
                        }
                      />
                    }
                    label="Join Key"
                  />
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <IconButton
                    size="small"
                    onClick={() => moveField(sectionKey, idx, "up")}
                    disabled={idx === 0}
                    aria-label="move-field-up"
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveField(sectionKey, idx, "down")}
                    disabled={idx === rows.length - 1}
                    aria-label="move-field-down"
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeField(sectionKey, idx)}
                >
                  Remove
                </Button>
              </Box>
            );
          })}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 1,
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            mappingProfile
          </Typography>
          <Tooltip
            placement="right"
            title={
              <Box sx={{ p: 0.5, maxWidth: 420 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                  Transform Field Guide
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  What to type in Transform:
                </Typography>
                <Typography sx={{ fontSize: 12 }}>upper</Typography>
                <Typography sx={{ fontSize: 12 }}>lower</Typography>
                <Typography sx={{ fontSize: 12 }}>trim</Typography>
                <Typography sx={{ fontSize: 12 }}>dateFormat</Typography>
                <Typography sx={{ fontSize: 12 }}>numberFormat</Typography>
                <Typography sx={{ fontSize: 12, mb: 0.5 }}>
                  currencyFormat
                </Typography>

                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Names that usually need args:
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  lookup, padLeft, padRight, booleanMap, substring, concat
                </Typography>
                <Typography sx={{ fontSize: 11, mb: 0.5 }}>
                  Transform arguments are not configurable in this screen.
                </Typography>

                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Transform examples:
                </Typography>
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  transform: {"{"} name: "dateFormat", args: {"{"} pattern:
                  "yyyy-MM-dd" {"}"} {"}"}
                </Typography>
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  transform: {"{"} name: "padLeft", args: {"{"} length: 6, char:
                  "0" {"}"} {"}"}
                </Typography>
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  transform: {"{"} name: "lookup", args: {"{"} map: {"{"} A:
                  "Alpha" {"}"}, defaultValue: "Unknown" {"}"} {"}"}
                </Typography>
                <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                  transform: {"{"} name: "booleanMap", args: {"{"} trueValue:
                  "Y", falseValue: "N" {"}"} {"}"}
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Input
            label="Version"
            value={mapping.version || "v1"}
            onChange={(value) =>
              updateSectionValue("mappingProfile", "version", value)
            }
          />
          <Select
            label="Mode"
            value={mapping.mode || "json_single"}
            onChange={(value) =>
              updateSectionValue("mappingProfile", "mode", value)
            }
            items={MAPPING_MODES}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={mapping.quoteHeaders !== false}
                onChange={(_event, checked) =>
                  updateSectionValue("mappingProfile", "quoteHeaders", checked)
                }
              />
            }
            label="Quote CSV Headers"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Transform field accepts transform name only (for example: upper,
            trim, dateFormat). Value Type supports Path or Constant for fixed
            columns (for example customer number, currency).
          </Typography>
          {renderFieldSection("Header Fields", "headerFields", headerFields)}
          {renderFieldSection("Detail Fields", "detailFields", detailFields)}
          {renderFieldSection("Trailer Fields", "trailerFields", trailerFields)}
        </Box>
      </Box>
    );
  };

  const renderPoNumberConfigEditor = () => {
    const poConfig = {
      ...getDefaultPoNumberConfig(),
      ...getSectionObject("poNumberConfig"),
    };

    return (
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 1,
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
          poNumberConfig
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Input
            label="PO Prefix"
            value={poConfig.prefix || ""}
            onChange={(value) =>
              updateSectionValue("poNumberConfig", "prefix", value)
            }
          />
          <Input
            label="Separator"
            value={poConfig.separator || ""}
            onChange={(value) =>
              updateSectionValue("poNumberConfig", "separator", value)
            }
          />
          <Input
            label="Pad Length"
            value={String(poConfig.padLength ?? 0)}
            onChange={(value) =>
              updateSectionValue(
                "poNumberConfig",
                "padLength",
                Number(value || 0),
              )
            }
          />
          <Input
            label="Start At"
            value={String(poConfig.startAt ?? 1)}
            onChange={(value) =>
              updateSectionValue(
                "poNumberConfig",
                "startAt",
                Number(value || 1),
              )
            }
          />
          <Input
            label="Last PO Number"
            value={String(poConfig.lastPoNumber ?? 0)}
            disabled={true}
          />
        </Box>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Last PO Number is system-managed and is not changed by profile edits.
        </Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Next PO uses this formula: prefix + separator + padded(sequence),
          where sequence increments from lastPoNumber + 1.
        </Typography>
      </Box>
    );
  };

  const saveProfile = async () => {
    if (!clientId) {
      showMessage("Missing client context", "error");
      return;
    }

    const validation = runValidation();
    if (!validation.isValid) {
      showMessage("Please fix validation errors before saving.", "error");
      return;
    }

    const serverValidation = await runServerValidation(
      validation.parsedSections,
    );
    if (!serverValidation.isValid) {
      showMessage("Server validation errors found.", "error");
      return;
    }

    const payload = {
      clientId,
      name: String(formData.name || "").trim(),
      isActive: !!formData.isActive,
      transportType: formData.transportType,
      payloadFormatVersion: String(formData.payloadFormatVersion || "").trim(),
      authConfig: validation.parsedSections.authConfig,
      endpointConfig: validation.parsedSections.endpointConfig,
      retryConfig: validation.parsedSections.retryConfig,
      ackConfig: validation.parsedSections.ackConfig,
      poNumberConfig: validation.parsedSections.poNumberConfig,
      mappingProfile: validation.parsedSections.mappingProfile,
    };

    setSaving(true);
    try {
      const results = await executeAction({
        entityName: "ClientTransmissionProfile",
        action: editingRecord?.id ? "update" : "add",
        data: editingRecord?.id
          ? { ...payload, id: editingRecord.id }
          : payload,
      });

      if (!results?.isSuccessful) {
        showMessage(
          results?.message || "Failed to save transmission profile",
          "error",
        );
        return;
      }

      showMessage("Transmission profile saved", "success");
      setOpenDialog(false);
      await loadProfiles();
    } catch (err) {
      showMessage(
        err?.message || "Failed to save transmission profile",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "add") {
      openAddDialog();
      return;
    }

    if (name === "refresh") {
      loadProfiles();
    }
  };

  const handleRowClick = (_column, id) => {
    const selected = rows.find((row) => row.id === id);
    if (!selected) return;
    openEditDialog(selected);
  };

  const columns = [
    { name: "name", label: "Name", clickable: true, width: "260px" },
    {
      name: "transportType",
      label: "Transport",
      clickable: true,
      width: "140px",
    },
    {
      name: "payloadFormatVersion",
      label: "Payload Version",
      width: "160px",
    },
    {
      name: "isActive",
      label: "Active",
      width: "120px",
      render: (row) => (row?.isActive ? "Yes" : "No"),
    },
  ];

  return (
    <>
      <Page
        pageName="Transmission Profiles"
        entityName={clientName || ""}
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[
          { name: "back", label: "Back", iconName: "ArrowBack" },
          { name: "add", label: "Add", iconName: "AddCircleOutline" },
          { name: "refresh", label: "Refresh", iconName: "Refresh" },
        ]}
      >
        {!clientId ? (
          <Typography>Client context is missing for this page.</Typography>
        ) : (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Configure client transmission profiles. JSON sections can be
              templated, formatted, validated, and saved.
            </Typography>
            {loading ? (
              <Typography>Loading transmission profiles...</Typography>
            ) : (
              <EntityList
                entityList={sortedRows}
                columns={columns}
                onRowClick={handleRowClick}
                tableHeight="calc(100vh - 250px)"
                allowSort={false}
              />
            )}
          </Box>
        )}
      </Page>

      <Dialog_Flex
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={
          editingRecord?.id
            ? "Edit Transmission Profile"
            : "Add Transmission Profile"
        }
        message=""
        actions={[
          { name: "cancel", label: "cancel" },
          { name: "validate", label: "validate" },
          { name: "preview", label: previewLoading ? "previewing" : "preview" },
          { name: "save", label: saving ? "saving" : "save" },
        ]}
        onAction={async (action) => {
          if (action === "cancel") {
            setOpenDialog(false);
            return;
          }

          if (action === "validate") {
            const localValidation = runValidation();
            if (!localValidation.isValid) {
              showMessage("Validation errors found", "error");
              return;
            }

            const serverValidation = await runServerValidation(
              localValidation.parsedSections,
            );

            if (serverValidation.isValid) {
              if (serverValidation.warnings.length > 0) {
                showMessage("Profile is valid with warnings", "info");
              } else {
                showMessage("Profile is valid", "success");
              }
            } else {
              showMessage("Validation errors found", "error");
            }
            return;
          }

          if (action === "preview" && !previewLoading) {
            const localValidation = runValidation();
            if (!localValidation.isValid) {
              showMessage("Validation errors found", "error");
              return;
            }

            const serverValidation = await runServerValidation(
              localValidation.parsedSections,
            );
            if (!serverValidation.isValid) {
              showMessage("Validation errors found", "error");
              return;
            }

            const preview = await runPayloadPreview(
              localValidation.parsedSections,
            );
            if (preview.isValid) {
              showMessage("Payload preview is valid", "success");
            } else {
              showMessage("Payload preview has blocking errors", "error");
            }
            return;
          }

          if (action === "save" && !saving) {
            saveProfile();
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            minWidth: 760,
            maxWidth: 920,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Input
              label="Name"
              value={formData.name}
              onChange={(value) => updateField("name", value)}
            />

            <Input
              label="Payload Format Version"
              value={formData.payloadFormatVersion}
              onChange={(value) => updateField("payloadFormatVersion", value)}
            />
          </Box>

          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1 }}
          >
            <Select
              label="Transport Type"
              value={formData.transportType}
              items={TRANSPORT_ITEMS}
              onChange={(value) => {
                const template = getTemplatesByTransport(value);
                setFormData((previous) => ({
                  ...previous,
                  transportType: value,
                  authConfig: stringifyPretty(template.authConfig),
                  endpointConfig: stringifyPretty(template.endpointConfig),
                  retryConfig: stringifyPretty(template.retryConfig),
                  ackConfig: stringifyPretty(template.ackConfig),
                }));
              }}
            />

            <Box sx={{ display: "flex", alignItems: "end", pb: 0.3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.isActive}
                    onChange={(_event, checked) =>
                      updateField("isActive", checked)
                    }
                  />
                }
                label="Active"
              />
            </Box>
          </Box>

          {validationErrors.length > 0 && (
            <Box
              sx={{
                border: "1px solid #ef9a9a",
                backgroundColor: "#ffebee",
                borderRadius: 1,
                px: 1,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                Validation Errors
              </Typography>
              {validationErrors.map((entry, idx) => (
                <Typography
                  key={`${entry}-${idx}`}
                  sx={{ fontSize: 12 }}
                >
                  {entry}
                </Typography>
              ))}
            </Box>
          )}

          {previewResult && (
            <Box
              sx={{
                border: "1px solid #bdbdbd",
                backgroundColor: "#fafafa",
                borderRadius: 1,
                px: 1,
                py: 1,
              }}
            >
              {(() => {
                const previewErrors = Array.isArray(previewResult?.errors)
                  ? previewResult.errors
                  : [];
                const issueLines =
                  previewErrors.length > 0
                    ? previewErrors.map(formatServerValidationError)
                    : previewResult?.isValid === false
                      ? [
                          "Payload preview is invalid but no detailed error was returned.",
                        ]
                      : [];

                return issueLines.length > 0 ? (
                  <Box
                    sx={{
                      border: "1px solid #ef9a9a",
                      backgroundColor: "#ffebee",
                      borderRadius: 1,
                      px: 1,
                      py: 1,
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                      Preview Issues
                    </Typography>
                    {issueLines.map((line, idx) => (
                      <Typography
                        key={`preview-issue-${idx}`}
                        sx={{ fontSize: 12 }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                ) : null;
              })()}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Payload Preview
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowPreviewJson((previous) => !previous)}
                >
                  {showPreviewJson ? "Hide JSON" : "Show JSON"}
                </Button>
              </Box>
              <Typography sx={{ fontSize: 12 }}>
                Status: {previewResult?.isValid ? "valid" : "invalid"}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Mode: {previewResult?.renderedPayload?.mode || "n/a"}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Version: {previewResult?.renderedPayload?.version || "n/a"}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Payload Hash: {previewResult?.payloadHash || "n/a"}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Header Columns:{" "}
                {previewResult?.payloadMetadata?.headerColumnCount ?? 0}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Detail Columns:{" "}
                {previewResult?.payloadMetadata?.detailColumnCount ?? 0}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Detail Rows:{" "}
                {previewResult?.payloadMetadata?.detailRowCount ?? 0}
              </Typography>

              {showPreviewJson && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <TextField
                    label="Header JSON"
                    value={JSON.stringify(
                      previewResult?.renderedPayload?.header || {},
                      null,
                      2,
                    )}
                    multiline
                    minRows={5}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Details JSON"
                    value={JSON.stringify(
                      previewResult?.renderedPayload?.details || [],
                      null,
                      2,
                    )}
                    multiline
                    minRows={7}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Trailer JSON"
                    value={JSON.stringify(
                      previewResult?.renderedPayload?.trailer || {},
                      null,
                      2,
                    )}
                    multiline
                    minRows={4}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              )}
            </Box>
          )}

          <>
            {renderAuthConfigEditor()}
            {renderEndpointConfigEditor()}
            {renderPoNumberConfigEditor()}
            {renderMappingProfileEditor()}
          </>
        </Box>
      </Dialog_Flex>
    </>
  );
};

export default OrdersTransmissionProfilesPage;
